import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Drawer as DrawerPrimitive } from "vaul";
import { useIsDesktop } from "@/hooks/use-is-desktop";
import { useBackDismiss } from "@/hooks/use-back-dismiss";
import { cn } from "@/lib/cn";

const ModalContext = React.createContext<{
  active: boolean;
  dismissible: boolean;
}>({ active: false, dismissible: true });

type DrawerProps = React.ComponentProps<typeof DrawerPrimitive.Root> & {
  desktopModal?: boolean;
  backDismiss?: boolean;
};

const Drawer = ({
  shouldScaleBackground = true,
  repositionInputs = false,
  desktopModal = true,
  backDismiss = true,
  ...props
}: DrawerProps) => {
  const asModal = useIsDesktop() && desktopModal;
  const dismissible = props.dismissible ?? true;
  const mode = React.useMemo(
    () => ({ active: asModal, dismissible }),
    [asModal, dismissible],
  );

  // Every consumer drives its drawer with `open`, but the uncontrolled form is
  // part of the primitive's contract, so mirror the state either way — the
  // back button needs to know whether the drawer is on screen.
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(
    props.open ?? props.defaultOpen ?? false,
  );
  const open = props.open ?? uncontrolledOpen;
  const onOpenChange = props.onOpenChange;
  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [onOpenChange],
  );

  useBackDismiss(open && backDismiss && dismissible, () =>
    handleOpenChange(false),
  );

  return (
    <ModalContext.Provider value={mode}>
      {asModal ? (
        <DialogPrimitive.Root
          open={props.open}
          defaultOpen={props.defaultOpen}
          onOpenChange={handleOpenChange}
          modal={props.modal}
        >
          {props.children}
        </DialogPrimitive.Root>
      ) : (
        <DrawerPrimitive.Root
          shouldScaleBackground={shouldScaleBackground}
          repositionInputs={repositionInputs}
          {...props}
          onOpenChange={handleOpenChange}
        />
      )}
    </ModalContext.Provider>
  );
};
Drawer.displayName = "Drawer";

// Vaul re-exports Radix's own primitives for these, so one binding serves both
// modes — each reads the dialog context its root put in place.
const DrawerTrigger = DrawerPrimitive.Trigger;
const DrawerClose = DrawerPrimitive.Close;

const DrawerPortal = ({
  children,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Portal>) => {
  const { active } = React.useContext(ModalContext);
  const Portal = active ? DialogPrimitive.Portal : DrawerPrimitive.Portal;
  return <Portal {...props}>{children}</Portal>;
};

const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>(({ className, ...props }, ref) => {
  const { active } = React.useContext(ModalContext);
  const Overlay = active ? DialogPrimitive.Overlay : DrawerPrimitive.Overlay;
  return (
    <Overlay
      ref={ref}
      className={cn(
        "fixed inset-0 z-40 bg-black/40",
        // Vaul animates its own overlay; the dialog one needs to be told to.
        active &&
          "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
        className,
      )}
      {...props}
    />
  );
});
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName;

type DrawerContentProps = React.ComponentPropsWithoutRef<
  typeof DrawerPrimitive.Content
> & {
  /** Hide the drag-handle pill at the top of the sheet. Ignored when modal. */
  hideHandle?: boolean;
};

const DrawerContent = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Content>,
  DrawerContentProps
>(({ className, children, hideHandle, ...props }, ref) => {
  const { active, dismissible } = React.useContext(ModalContext);

  if (active) {
    const block = dismissible
      ? undefined
      : (event: Event) => event.preventDefault();
    return (
      <DrawerPortal>
        <DrawerOverlay />
        {/* Centring wrapper rather than a translate on the content itself, so
            the zoom animation has the transform to itself. Click-through lets
            the overlay beneath still catch outside clicks. */}
        <div className="pointer-events-none fixed inset-0 z-50 grid place-items-center p-4">
          <DialogPrimitive.Content
            ref={ref}
            onEscapeKeyDown={block}
            onInteractOutside={block}
            className={cn(
              "pointer-events-auto w-full max-w-md max-h-[85vh] overflow-y-auto rounded-2xl border border-default-100 bg-background p-5 shadow-xl outline-none",
              "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
              className,
              // Margins are sheet positioning (`mt-24` and friends) — the grid
              // above does the centring here.
              "m-0",
            )}
            {...props}
          >
            {children}
          </DialogPrimitive.Content>
        </div>
      </DrawerPortal>
    );
  }

  return (
    <DrawerPortal>
      <DrawerOverlay />
      <DrawerPrimitive.Content
        ref={ref}
        className={cn(
          "fixed bottom-0 left-0 right-0 z-50 mx-auto max-w-xl rounded-t-2xl bg-background p-4 pt-3 outline-none",
          className,
        )}
        {...props}
      >
        {hideHandle ? null : (
          <div className="mx-auto mb-3 h-1 w-12 rounded-full bg-default-200" />
        )}
        {children}
        <div className="safe-area-inset-bottom w-full"></div>
      </DrawerPrimitive.Content>
    </DrawerPortal>
  );
});
DrawerContent.displayName = "DrawerContent";

const DrawerHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("flex flex-col gap-1.5 text-right", className)}
    {...props}
  />
);
DrawerHeader.displayName = "DrawerHeader";

const DrawerFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("mt-5 flex gap-3", className)} {...props} />
);
DrawerFooter.displayName = "DrawerFooter";

const DrawerTitle = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Title
    ref={ref}
    className={cn("text-sm font-bold text-default-900", className)}
    {...props}
  />
));
DrawerTitle.displayName = DrawerPrimitive.Title.displayName;

const DrawerDescription = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Description
    ref={ref}
    className={cn("text-xs leading-6 text-default-500", className)}
    {...props}
  />
));
DrawerDescription.displayName = DrawerPrimitive.Description.displayName;

export {
  Drawer,
  DrawerPortal,
  DrawerOverlay,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
};
